// @ts-nocheck
// Ported byte-identically from v1 views/dashboard/call-group/spend-analytics.js.
// Route: /call-group-analytics (ROUTES.MANAGE_AGENTS.AGENT_GROUP_ANALYTICS).
//
// Chrome swaps (mirror the sibling CallCentreSpendAnalyticsPage port):
//   - PrimeReact <DataTable>/<Column> â†’ obd3-gui @/components/data-table
//     (TanStack). All 7 columns preserved with same headers, sort behavior,
//     and cell renderers. paginator=true rows=10 â†’ pageSize={10}.
//   - v1 CSS classes (cg-analytics, cc-spend-analytics, cc-kpi, etc.) are
//     kept as markers on the same elements. Essential visual polish is
//     applied via inline styles â€” matches the pattern used in
//     features/call-centres/CallCentreSpendAnalyticsPage.tsx so the two
//     sibling pages read alike. Full v1 CSS (~750 lines) is not bundled;
//     rendering is structurally correct and consistent with sibling.
//   - v1's custom.css side-effect import dropped.
// Behavior preserved verbatim: ACL gating on MODULE_ACL.CALL_GROUP,
// aggregateSpend day/week/month bucket logic, TrendMini/TrendPill arrows,
// CSV export column order and filename, breakdown modal donut + stat cards.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const _cx = (...args: any[]) => args.filter(Boolean).join(' ');
const Row: any = ({ className, children, ...props }: any) => (
  <div className={_cx('row', className)} {...props}>
    {children}
  </div>
);
const _colCls = ({ xs, sm, md, lg, xl }: any) => {
  const parts = [];
  if (md) parts.push(`col-md-${md}`);
  if (lg) parts.push(`col-lg-${lg}`);
  if (xl) parts.push(`col-xl-${xl}`);
  if (sm) parts.push(`col-sm-${sm}`);
  if (xs) parts.push(`col-${xs}`);
  if (!parts.length) parts.push('col');
  return parts.join(' ');
};
const Col: any = ({ xs, sm, md, lg, xl, className, children, ...props }: any) => (
  <div className={_cx(_colCls({ xs, sm, md, lg, xl }), className)} {...props}>
    {children}
  </div>
);
const Modal: any = ({ show, onHide, size, centered: _c, children, className, ...props }: any) => {
  const sizeCls =
    size === 'sm'
      ? 'sm:max-w-md'
      : size === 'lg'
      ? 'sm:max-w-2xl'
      : size === 'xl'
      ? 'sm:max-w-6xl'
      : '';
  return (
    <Dialog open={!!show} onOpenChange={(o: boolean) => !o && onHide?.()}>
      <DialogContent
        className={`${sizeCls} max-h-[90vh] overflow-y-auto ${className || ''}`.trim()}
        {...props}
      >
        {children}
      </DialogContent>
    </Dialog>
  );
};
Modal.Header = ({ children, closeButton: _cb, className, ...props }: any) => (
  <DialogHeader className={className} {...props}>
    {children}
  </DialogHeader>
);
Modal.Title = ({ children, className, ...props }: any) => (
  <DialogTitle className={className} {...props}>
    {children}
  </DialogTitle>
);
Modal.Body = ({ children, className, ...props }: any) => (
  <div className={`overflow-x-auto ${className || ''}`.trim()} {...props}>
    {children}
  </div>
);
Modal.Footer = ({ children, className, ...props }: any) => (
  <DialogFooter className={className} {...props}>
    {children}
  </DialogFooter>
);
import type { ColumnDef } from '@tanstack/react-table';
import { Card } from '@/components/ui/card';
import { DataTable } from '@/components/data-table/DataTable';
import Loader from '@/components/partials/components/Loader';
import { getApiCall } from '@/service/httpCall';
import { API_CALL_GROUP_SPEND_ANALYTICS } from '@/service/url';
import { formatRupeeAmount, hasPermission } from '@/utilities/helpers';
import { MODULE_ACL } from '@/utilities/constant';
import { getUser } from '@/utilities/setting';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
} from 'recharts';
import {
  FiCreditCard,
  FiTrendingUp,
  FiPhoneCall,
  FiUsers,
  FiCalendar,
  FiFilter,
  FiDownload,
  FiRefreshCw,
  FiBarChart2,
  FiArrowDownLeft,
  FiArrowUpRight,
  FiMoreHorizontal,
} from 'react-icons/fi';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { Button } from '@/components/ui/button';
import { BarChart2, Building2, TrendingDown, TrendingUp } from 'lucide-react';
import { parseModuleOperations } from '@/utilities/voicePlanAcl';

const BREAKDOWN = [
  { key: 'incoming', label: 'Incoming', color: '#3a57e8', icon: <FiArrowDownLeft size={16} /> },
  { key: 'outgoing', label: 'Outgoing', color: '#16a34a', icon: <FiArrowUpRight size={16} /> },
  { key: 'ivr', label: 'IVR', color: '#f59e0b', icon: <FiPhoneCall size={16} /> },
  { key: 'others', label: 'Others', color: '#94a3b8', icon: <FiMoreHorizontal size={16} /> },
];

const groupKeyOf = (g: any) => String(g?.id ?? g?.name ?? '');

const displayName = (name: any, row: any) => {
  if (row?.unassigned || name === 'Unassigned') return 'Unassigned (no call group)';
  if (row?.orphan) return `${name} (legacy)`;
  return name;
};

const fmtRangeLabel = (period: any) => {
  if (!period?.month) return '';
  const s = new Date(`${period.month.start}T00:00:00`);
  const e = new Date(`${period.month.end}T00:00:00`);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return '';
  const sL = s.toLocaleDateString('en-IN', { month: 'short', day: '2-digit' });
  const eL = e.toLocaleDateString('en-IN', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  });
  return `${sL} – ${eL} (30d)`;
};

const startOfWeek = (d: any) => {
  const dt = new Date(d);
  const dow = (dt.getDay() + 6) % 7;
  dt.setDate(dt.getDate() - dow);
  dt.setHours(0, 0, 0, 0);
  return dt;
};

const aggregateSpend = (daily: any, group: string) => {
  const list = Array.isArray(daily) ? daily : [];
  if (group === 'day') {
    return list.map((d: any) => {
      const dt = new Date(`${d.date}T00:00:00`);
      return {
        label: dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }),
        fullLabel: dt.toLocaleDateString('en-IN', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        }),
        incoming: Number(d.incoming || 0),
        outgoing: Number(d.outgoing || 0),
        ivr: Number(d.ivr || 0),
        others: Number(d.others || 0),
      };
    });
  }
  const map = new Map<string, any>();
  for (const d of list) {
    const dt = new Date(`${d.date}T00:00:00`);
    let key: string;
    let label: string;
    let fullLabel: string;
    if (group === 'week') {
      const s = startOfWeek(dt);
      key = s.toISOString().slice(0, 10);
      label = s.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
      fullLabel = `Week of ${s.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })}`;
    } else {
      key = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
      label = dt.toLocaleDateString('en-IN', { month: 'short', year: '2-digit' });
      fullLabel = dt.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
    }
    if (!map.has(key)) {
      map.set(key, { key, label, fullLabel, incoming: 0, outgoing: 0, ivr: 0, others: 0 });
    }
    const agg = map.get(key);
    agg.incoming += Number(d.incoming || 0);
    agg.outgoing += Number(d.outgoing || 0);
    agg.ivr += Number(d.ivr || 0);
    agg.others += Number(d.others || 0);
  }
  return Array.from(map.values())
    .sort((a: any, b: any) => a.key.localeCompare(b.key))
    .map((r: any) => ({
      ...r,
      incoming: Number(r.incoming.toFixed(2)),
      outgoing: Number(r.outgoing.toFixed(2)),
      ivr: Number(r.ivr.toFixed(2)),
      others: Number(r.others.toFixed(2)),
    }));
};

const SpendTooltip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload || {};
  const total =
    Number(row.incoming || 0) +
    Number(row.outgoing || 0) +
    Number(row.ivr || 0) +
    Number(row.others || 0);
  return (
    <div
      className="cc-tt"
      style={{
        background: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: 8,
        padding: 10,
        boxShadow: '0 4px 12px rgba(15,23,42,0.08)',
        fontSize: 12,
      }}
    >
      <div className="cc-tt__head" style={{ fontWeight: 700, marginBottom: 6 }}>
        {row.fullLabel || row.label}
      </div>
      {BREAKDOWN.map((s) => {
        const val = Number(row[s.key] || 0);
        const pct = total > 0 ? Math.round((val / total) * 100) : 0;
        return (
          <div
            className="cc-tt__row"
            key={s.key}
            style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '2px 0' }}
          >
            <span
              className="cc-tt__left"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <span
                className="cc-tt__dot"
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: s.color,
                  display: 'inline-block',
                }}
              />
              {s.label}
            </span>
            <span className="cc-tt__right" style={{ fontWeight: 600 }}>
              {formatRupeeAmount(val)}
              <span className="cc-tt__pct" style={{ marginLeft: 4, color: '#94a3b8' }}>
                ({pct}%)
              </span>
            </span>
          </div>
        );
      })}
      <div
        className="cc-tt__total"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          borderTop: '1px dashed #e2e8f0',
          marginTop: 6,
          paddingTop: 6,
          fontWeight: 700,
        }}
      >
        <span>Total</span>
        <span>{formatRupeeAmount(total)}</span>
      </div>
    </div>
  );
};

const Sparkline = ({ data, dataKey, color, gradientId }: any) => (
  <div className="cc-kpi__spark">
    <ResponsiveContainer width="100%" height={34}>
      <AreaChart data={data} margin={{ top: 6, right: 0, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.35} />
            <stop offset="100%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <Area
          type="monotone"
          dataKey={dataKey}
          stroke={color}
          strokeWidth={2}
          fill={`url(#${gradientId})`}
          dot={false}
          isAnimationActive={false}
        />
      </AreaChart>
    </ResponsiveContainer>
  </div>
);

const TrendPill = ({ value, suffix, goodDirection = 'up', note }: any) => {
  if (note) {
    return (
      <div
        className="cc-kpi__trend cc-kpi__trend--note"
        style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}
      >
        {note}
      </div>
    );
  }
  const v = Number(value) || 0;
  const up = v >= 0;
  const isGood = goodDirection === 'up' ? up : !up;
  const Icon = up ? TrendingUp : TrendingDown;
  return (
    <div className="mt-1.5 flex items-center gap-1.5 text-[11px]">
      {suffix ? <span className="text-muted-foreground">{suffix}</span> : null}
      <span
        className={
          'inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-semibold ring-1 ring-inset ' +
          (isGood
            ? 'bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300'
            : 'bg-rose-100 text-rose-700 ring-rose-200 dark:bg-rose-950/40 dark:text-rose-300')
        }
      >
        <Icon className="h-3 w-3" aria-hidden />
        {Math.abs(v).toFixed(1)}%
      </span>
    </div>
  );
};

const TrendMini = ({ value }: any) => {
  const v = Number(value) || 0;
  const up = v >= 0;
  const Icon = up ? TrendingUp : TrendingDown;
  return (
    <span
      className={
        'ml-1.5 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset ' +
        (up
          ? 'bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300'
          : 'bg-rose-100 text-rose-700 ring-rose-200 dark:bg-rose-950/40 dark:text-rose-300')
      }
    >
      <Icon className="h-3 w-3" aria-hidden />
      {Math.abs(v).toFixed(1)}%
    </span>
  );
};

const CallGroupSpendAnalytics = () => {
  const [loading, setLoading] = useState(true);
  const [payload, setPayload] = useState<any>(null);
  const [moduleOps, setModuleOps] = useState<any>({});
  const [groupFilter, setGroupFilter] = useState('all');
  const [trendGroup, setTrendGroup] = useState('day');
  const [modalGroup, setModalGroup] = useState<any>(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    const user = getUser();
    if (user?.acl_settings?.module_operations) {
      try {
        const parsed =
          typeof user.acl_settings.module_operations === 'string'
            ? parseModuleOperations(user.acl_settings.module_operations)
            : user.acl_settings.module_operations;
        setModuleOps(parsed || {});
      } catch {
        setModuleOps({});
      }
    }
  }, []);

  const canRead =
    hasPermission(moduleOps, MODULE_ACL.CALL_GROUP, 'read') ||
    Boolean(moduleOps?.[MODULE_ACL.CALL_GROUP]?.view_permission);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const resp: any = await getApiCall(API_CALL_GROUP_SPEND_ANALYTICS, {});
      setPayload(resp?.data || resp);
    } catch (err: any) {
      console.error(err);
      toast.error(friendlyApiError(err) || 'Failed to load call group spend analytics');
      setPayload(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (Object.keys(moduleOps).length > 0 && canRead) {
      fetchData();
    } else if (Object.keys(moduleOps).length > 0 && !canRead) {
      setLoading(false);
    }
  }, [moduleOps, canRead, fetchData]);

  const groups = useMemo<any[]>(() => payload?.call_groups || [], [payload]);
  const totals = payload?.totals || {};
  const chart = payload?.chart || {};
  const cc = payload?.call_centre;
  const period = payload?.period;

  const dailyRaw = useMemo(() => chart.daily_last_month || [], [chart]);

  const chartData = useMemo(
    () => aggregateSpend(dailyRaw, trendGroup),
    [dailyRaw, trendGroup]
  );

  const tableGroups = useMemo(
    () =>
      groupFilter === 'all'
        ? groups
        : groups.filter((g: any) => groupKeyOf(g) === groupFilter),
    [groups, groupFilter]
  );

  const openBreakdown = (row: any) => {
    setModalGroup(row);
    setShowModal(true);
  };

  const modalBreakdown = modalGroup?.breakdown_last_month || {};
  const modalTotal =
    Number(modalBreakdown.incoming || 0) +
    Number(modalBreakdown.outgoing || 0) +
    Number(modalBreakdown.ivr || 0) +
    Number(modalBreakdown.others || 0);
  const modalPie = BREAKDOWN.map((b) => ({
    ...b,
    value: Number(modalBreakdown[b.key] || 0),
  })).filter((b) => b.value > 0);

  const exportCsv = () => {
    if (!groups.length) {
      toast.info('Nothing to export');
      return;
    }
    const headers = [
      'Call Group',
      'Spent 7d',
      'Trend 7d %',
      'Spent 30d',
      'Trend 30d %',
      'Calls 7d',
      'Calls 30d',
      '% of month',
    ];
    const cell = (v: any) => {
      const s = String(v ?? '');
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = groups.map((g: any) => {
      const pct = totals.spent_last_month
        ? (
            (Number(g.spent_last_month || 0) / Number(totals.spent_last_month)) *
            100
          ).toFixed(1)
        : '0';
      return [
        displayName(g.name, g),
        g.spent_last_week || 0,
        g.trend_spent_week ?? 0,
        g.spent_last_month || 0,
        g.trend_spent_month ?? 0,
        g.calls_last_week || 0,
        g.calls_last_month || 0,
        `${pct}%`,
      ];
    });
    const csv = [headers, ...rows].map((r: any) => r.map(cell).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `call-group-spend-${cc?.name || 'export'}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (Object.keys(moduleOps).length > 0 && !canRead) {
    return (
      <div className="text-center p-4" style={{ marginTop: 50, fontSize: 16 }}>
        No read permission found in assigned ACL
      </div>
    );
  }

  if (loading) return <Loader />;

  const kpis = [
    {
      key: 'spent_week',
      label: 'Spent (7 days)',
      value: formatRupeeAmount(totals.spent_last_week || 0),
      trend: totals.trend_spent_week,
      suffix: 'vs prev 7d',
      goodDirection: 'down',
      icon: <FiCreditCard size={18} />,
      variant: 'violet',
      color: '#7c3aed',
      dataKey: 'spent',
    },
    {
      key: 'spent_month',
      label: 'Spent (30 days)',
      value: formatRupeeAmount(totals.spent_last_month || 0),
      trend: totals.trend_spent_month,
      suffix: 'vs prev 30d',
      goodDirection: 'down',
      icon: <FiTrendingUp size={18} />,
      variant: 'green',
      color: '#16a34a',
      dataKey: 'spent',
    },
    {
      key: 'calls_week',
      label: 'Calls (7 days)',
      value: Number(totals.calls_last_week || 0).toLocaleString('en-IN'),
      trend: totals.trend_calls_week,
      suffix: 'vs prev 7d',
      goodDirection: 'up',
      icon: <FiPhoneCall size={18} />,
      variant: 'blue',
      color: '#3a57e8',
      dataKey: 'calls',
    },
    {
      key: 'groups',
      label: 'Call Groups',
      value: Number(cc?.call_group_count || groups.length || 0).toLocaleString('en-IN'),
      note: 'active groups',
      icon: <FiUsers size={18} />,
      variant: 'violet',
      color: '#8b5cf6',
      dataKey: 'spent',
    },
  ];

  const columns: ColumnDef<any>[] = [
    {
      accessorKey: 'name',
      header: 'Call Group',
      cell: ({ row }: any) => (
        <span className="cg-name" style={{ fontWeight: 600 }}>
          {displayName(row.original.name, row.original)}
        </span>
      ),
    },
    {
      accessorKey: 'spent_last_week',
      header: 'Spent (7d)',
      cell: ({ row }: any) => (
        <div className="cc-cell" style={{ display: 'inline-flex', alignItems: 'center' }}>
          <span className="cc-cell__val">
            {formatRupeeAmount(row.original.spent_last_week || 0)}
          </span>
          <TrendMini value={row.original.trend_spent_week} />
        </div>
      ),
    },
    {
      accessorKey: 'spent_last_month',
      header: 'Spent (30d)',
      cell: ({ row }: any) => (
        <div className="cc-cell" style={{ display: 'inline-flex', alignItems: 'center' }}>
          <span className="cc-cell__val">
            {formatRupeeAmount(row.original.spent_last_month || 0)}
          </span>
          <TrendMini value={row.original.trend_spent_month} />
        </div>
      ),
    },
    {
      accessorKey: 'calls_last_week',
      header: 'Calls (7d)',
      enableSorting: false,
      cell: ({ row }: any) => (
        <div className="cc-cell" style={{ display: 'inline-flex', alignItems: 'center' }}>
          <span className="cc-cell__val">
            {Number(row.original.calls_last_week || 0).toLocaleString('en-IN')}
          </span>
          <TrendMini value={row.original.trend_calls_week} />
        </div>
      ),
    },
    {
      accessorKey: 'calls_last_month',
      header: 'Calls (30d)',
      enableSorting: false,
      cell: ({ row }: any) => (
        <div className="cc-cell" style={{ display: 'inline-flex', alignItems: 'center' }}>
          <span className="cc-cell__val">
            {Number(row.original.calls_last_month || 0).toLocaleString('en-IN')}
          </span>
          <TrendMini value={row.original.trend_calls_month} />
        </div>
      ),
    },
    {
      id: 'pct_month',
      header: '% of month',
      enableSorting: false,
      cell: ({ row }: any) => {
        const total = Number(totals.spent_last_month || 0);
        const spent = Number(row.original.spent_last_month || 0);
        return total ? `${((spent / total) * 100).toFixed(1)}%` : '0%';
      },
    },
    {
      id: 'action',
      header: 'Action',
      enableSorting: false,
      enableHiding: false,
      cell: ({ row }: any) => (
        <button
          type="button"
          className="cg-action"
          title="View breakdown"
          onClick={() => openBreakdown(row.original)}
          style={{
            border: '1px solid #e2e8f0',
            background: '#fff',
            borderRadius: 6,
            width: 30,
            height: 30,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: '#3a57e8',
          }}
        >
          <FiBarChart2 size={15} />
        </button>
      ),
    },
  ];

  return (
    <FillPage>
      <PageHeader
        title="Call Group Spend Analytics"
        description={
          cc?.name
            ? `${cc.name} — spend by call group (last 7 / 30 days)`
            : 'Spend by call group (last 7 / 30 days).'
        }
        actions={
          <>
            <span
              className="inline-flex h-9 items-center gap-1.5 rounded-md border border-border/60 bg-card px-2.5 text-xs font-semibold text-muted-foreground"
              title="Active date range"
            >
              <FiCalendar size={13} />
              {fmtRangeLabel(period) || 'Last 30 days'}
            </span>
            <div className="relative">
              <FiFilter
                size={13}
                className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
              />
              <select
                value={groupFilter}
                onChange={(e) => setGroupFilter(e.target.value)}
                aria-label="Filter call group"
                className="input-embossed h-9 min-w-[180px] rounded-md pl-7 pr-3 text-xs"
              >
                <option value="all">All Call Groups</option>
                {groups.map((g: any) => (
                  <option key={groupKeyOf(g)} value={groupKeyOf(g)}>
                    {displayName(g.name, g)}
                  </option>
                ))}
              </select>
            </div>
            <Button variant="outline" onClick={exportCsv}>
              <FiDownload size={14} />
              Export
            </Button>
            <Button onClick={fetchData}>
              <FiRefreshCw size={14} />
              Refresh
            </Button>
          </>
        }
      />
      <div className="cc-spend-analytics cg-analytics">
      <Row>
        <Col sm="12">
          {!cc ? (
            <div
              className="flex flex-col items-center justify-center rounded-2xl border border-border/60 bg-card px-6 py-14 text-center"
              style={{
                boxShadow:
                  '6px 6px 14px rgba(163, 177, 198, 0.35), -6px -6px 14px rgba(255, 255, 255, 0.9)',
              }}
            >
              <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary ring-1 ring-inset ring-primary/25">
                <Building2 className="h-7 w-7" />
              </div>
              <h3 className="text-sm font-bold text-foreground">
                No call centre available
              </h3>
              <p className="mt-1 max-w-md text-xs text-muted-foreground">
                {payload?.message ||
                  'No call center assigned. Assign a call center to this admin to view spend.'}
              </p>
            </div>
          ) : (
            <>
              <div
                className="cc-spend-kpis"
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                  gap: 12,
                  marginBottom: 12,
                }}
              >
                {kpis.map((kpi: any) => (
                  <div
                    className="cc-kpi"
                    key={kpi.key}
                    style={{
                      border: '1px solid #e2e8f0',
                      borderRadius: 8,
                      padding: '12px 14px',
                      background: '#fff',
                    }}
                  >
                    <div
                      className="cc-kpi__head"
                      style={{ display: 'flex', alignItems: 'center', gap: 10 }}
                    >
                      <div
                        className={`cc-kpi__icon cc-kpi__icon--${kpi.variant}`}
                        style={{
                          width: 34,
                          height: 34,
                          borderRadius: 8,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: `${kpi.color}22`,
                          color: kpi.color,
                        }}
                      >
                        {kpi.icon}
                      </div>
                      <div className="cc-kpi__meta">
                        <div className="cc-kpi__label" style={{ fontSize: 12, color: '#64748b' }}>
                          {kpi.label}
                        </div>
                        <div className="cc-kpi__value" style={{ fontSize: 18, fontWeight: 700 }}>
                          {kpi.value}
                        </div>
                      </div>
                    </div>
                    <Sparkline
                      data={dailyRaw}
                      dataKey={kpi.dataKey}
                      color={kpi.color}
                      gradientId={`cg-spark-${kpi.key}`}
                    />
                    <TrendPill
                      value={kpi.trend}
                      suffix={kpi.suffix}
                      goodDirection={kpi.goodDirection}
                      note={kpi.note}
                    />
                  </div>
                ))}
              </div>

              <Card className="cc-spend-card p-3">
                <div
                  className="cc-section-head d-flex flex-wrap gap-2 align-items-center justify-content-between mb-3"
                >
                  <div className="cc-section-title" style={{ fontWeight: 600 }}>
                    Daily Spend Breakdown (30d)
                  </div>
                  <select
                    className="cc-groupby-select"
                    value={trendGroup}
                    onChange={(e) => setTrendGroup(e.target.value)}
                    aria-label="Group daily spend"
                    style={{
                      padding: '4px 8px',
                      borderRadius: 6,
                      border: '1px solid #e2e8f0',
                      background: '#fff',
                      fontSize: 13,
                    }}
                  >
                    <option value="day">Daily</option>
                    <option value="week">Weekly</option>
                    <option value="month">Monthly</option>
                  </select>
                </div>
                {chartData.length === 0 ? (
                  <div className="text-muted text-center py-5">
                    No usage data in this period
                  </div>
                ) : (
                  <>
                    <div style={{ width: '100%', height: 240 }}>
                      <ResponsiveContainer>
                        <BarChart
                          data={chartData}
                          margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
                          barCategoryGap="20%"
                        >
                          <CartesianGrid
                            strokeDasharray="3 3"
                            vertical={false}
                            stroke="#eef2f7"
                          />
                          <XAxis
                            dataKey="label"
                            tickLine={false}
                            axisLine={false}
                            fontSize={11}
                            minTickGap={12}
                            tick={{ fill: '#94a3b8' }}
                          />
                          <YAxis
                            tickLine={false}
                            axisLine={false}
                            fontSize={11}
                            width={48}
                            tick={{ fill: '#94a3b8' }}
                            tickFormatter={(v: any) => `\u20B9${Math.round(Number(v) || 0)}`}
                          />
                          <Tooltip
                            cursor={{ fill: 'rgba(148,163,184,0.08)' }}
                            content={<SpendTooltip />}
                          />
                          {BREAKDOWN.map((s, i) => (
                            <Bar
                              key={s.key}
                              dataKey={s.key}
                              stackId="a"
                              fill={s.color}
                              name={s.label}
                              maxBarSize={22}
                              radius={
                                i === BREAKDOWN.length - 1
                                  ? ([3, 3, 0, 0] as any)
                                  : undefined
                              }
                            />
                          ))}
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    <div
                      className="cc-legend"
                      style={{
                        display: 'flex',
                        gap: 14,
                        flexWrap: 'wrap',
                        justifyContent: 'center',
                        marginTop: 8,
                        fontSize: 12,
                        color: '#64748b',
                      }}
                    >
                      {BREAKDOWN.map((s) => (
                        <span
                          className="cc-legend__item"
                          key={s.key}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                        >
                          <span
                            className="cc-legend__dot"
                            style={{
                              display: 'inline-block',
                              width: 10,
                              height: 10,
                              borderRadius: '50%',
                              background: s.color,
                            }}
                          />
                          {s.label}
                        </span>
                      ))}
                    </div>
                  </>
                )}
              </Card>

              <Card className="cc-spend-card mt-3 p-3">
                <div
                  className="cc-section-head d-flex flex-wrap gap-2 align-items-center justify-content-between mb-3"
                >
                  <div className="cc-section-title" style={{ fontWeight: 600 }}>
                    Top call groups (30d summary)
                  </div>
                </div>
                <DataTable
                  tableId="call-group.spend-analytics"
                  columns={columns}
                  data={tableGroups}
                  pageSize={10}
                  emptyTitle="No call groups found"
                  disableExport
                />
              </Card>
            </>
          )}
        </Col>
      </Row>

      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        size="lg"
        centered
        dialogClassName="cg-breakdown-modal cg-analytics"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {modalGroup
              ? `${displayName(modalGroup.name, modalGroup)} - breakdown`
              : 'Group breakdown'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div
            className="cg-breakdown-sub"
            style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}
          >
            {modalGroup ? displayName(modalGroup.name, modalGroup) : ''} &bull; 30 days
          </div>
          {modalTotal === 0 ? (
            <div className="text-muted text-center py-4">No breakdown for this group</div>
          ) : (
            <div className="cg-breakdown">
              <div
                className="cg-breakdown__chart"
                style={{
                  display: 'grid',
                  gridTemplateColumns: '220px 1fr',
                  gap: 20,
                  alignItems: 'center',
                }}
              >
                <div
                  className="cg-donut cg-donut--sm"
                  style={{ position: 'relative', width: 220, height: 180 }}
                >
                  <ResponsiveContainer width="100%" height={180}>
                    <PieChart>
                      <Pie
                        data={modalPie}
                        dataKey="value"
                        nameKey="label"
                        cx="50%"
                        cy="50%"
                        innerRadius={52}
                        outerRadius={78}
                        paddingAngle={1}
                        stroke="none"
                      >
                        {modalPie.map((d: any) => (
                          <Cell key={d.key} fill={d.color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v: any) => formatRupeeAmount(v)} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div
                    className="cg-donut__center"
                    style={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      pointerEvents: 'none',
                    }}
                  >
                    <div className="cg-donut__value" style={{ fontWeight: 700 }}>
                      {formatRupeeAmount(modalTotal)}
                    </div>
                    <div className="cg-donut__label" style={{ fontSize: 11, color: '#64748b' }}>
                      Total
                    </div>
                  </div>
                </div>
                <ul
                  className="cg-breakdown__legend"
                  style={{ listStyle: 'none', margin: 0, padding: 0 }}
                >
                  {BREAKDOWN.map((b) => {
                    const val = Number(modalBreakdown[b.key] || 0);
                    const pct =
                      modalTotal > 0 ? ((val / modalTotal) * 100).toFixed(1) : '0.0';
                    return (
                      <li
                        key={b.key}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          padding: '6px 0',
                          borderBottom: '1px solid #f1f5f9',
                          fontSize: 13,
                        }}
                      >
                        <span
                          className="cg-legend-left"
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
                        >
                          <span
                            className="cg-dot"
                            style={{
                              width: 10,
                              height: 10,
                              borderRadius: '50%',
                              background: b.color,
                              display: 'inline-block',
                            }}
                          />
                          {b.label}
                        </span>
                        <span className="cg-legend-right" style={{ fontWeight: 600 }}>
                          {formatRupeeAmount(val)}
                          <span
                            className="cg-muted"
                            style={{ marginLeft: 4, color: '#94a3b8', fontWeight: 500 }}
                          >
                            ({pct}%)
                          </span>
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>

              <div
                className="cg-breakdown__cards"
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                  gap: 10,
                  marginTop: 16,
                }}
              >
                {BREAKDOWN.map((b) => {
                  const val = Number(modalBreakdown[b.key] || 0);
                  const pct =
                    modalTotal > 0 ? ((val / modalTotal) * 100).toFixed(1) : '0.0';
                  return (
                    <div
                      className="cg-stat"
                      key={b.key}
                      style={{
                        border: '1px solid #e2e8f0',
                        borderRadius: 8,
                        padding: 12,
                        background: '#fff',
                      }}
                    >
                      <div
                        className="cg-stat__icon"
                        style={{
                          width: 30,
                          height: 30,
                          borderRadius: 6,
                          background: `${b.color}1f`,
                          color: b.color,
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginBottom: 6,
                        }}
                      >
                        {b.icon}
                      </div>
                      <div className="cg-stat__label" style={{ fontSize: 12, color: '#64748b' }}>
                        {b.label}
                      </div>
                      <div
                        className="cg-stat__value"
                        style={{ fontSize: 16, fontWeight: 700, marginTop: 2 }}
                      >
                        {formatRupeeAmount(val)}
                      </div>
                      <div
                        className="cg-stat__sub"
                        style={{ fontSize: 11, color: '#98a1b3', marginTop: 2 }}
                      >
                        {pct}% of total
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>
      </div>
    </FillPage>
  );
};

export default CallGroupSpendAnalytics;
